import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  FileSpreadsheet,
  FileText,
  Info,
  Loader2,
  User as UsuarioIcono,
  Wallet,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando, CargandoEnLinea } from '@/shared/ui/Cargando';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Pagina } from '@/shared/api/cliente';
import type { DeudorEnCartera, ResumenDeCartera } from '@/shared/api/tipos';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { POR_PAGINA, Paginador } from '@/shared/ui/Paginador';
import { notificar } from '@/shared/ui/avisos';

export function PaginaCartera() {
  const [pagina, setPagina] = useState(0);
  const [bajando, setBajando] = useState<'pdf' | 'xlsx' | null>(null);
  const [servicio, setServicio] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  // Lo vacio no viaja: el servidor distingue "sin filtro" de "filtro vacio".
  const filtros = {
    ...(servicio ? { servicio } : {}),
    ...(desde ? { desde } : {}),
    ...(hasta ? { hasta } : {}),
  };
  const hayFiltros = Object.keys(filtros).length > 0;

  // Los grupos los decide el servidor: son los de la ordenanza, no los conceptos sueltos
  // del catalogo. Repetirlos aqui seria tener la misma regla en dos sitios.
  const servicios = useQuery({
    queryKey: ['cartera-servicios'],
    queryFn: () => api.get<{ codigo: string; nombre: string }[]>('/cartera/servicios'),
  });

  const resumen = useQuery({
    queryKey: ['cartera-resumen', filtros],
    queryFn: () => api.get<ResumenDeCartera>('/cartera/resumen', { query: filtros }),
  });

  const deudores = useQuery({
    queryKey: ['cartera-deudores', pagina, filtros],
    queryFn: () =>
      api.get<Pagina<DeudorEnCartera>>('/cartera/deudores', {
        query: { ...filtros, pagina, tamanio: POR_PAGINA },
      }),
  });

  function filtrar(cambio: () => void) {
    cambio();
    setPagina(0);
  }

  async function descargar(formato: 'pdf' | 'xlsx') {
    setBajando(formato);
    try {
      const consulta = new URLSearchParams(filtros).toString();
      const fichero = await api.descargar(
        `/cartera/reporte.${formato}${consulta ? `?${consulta}` : ''}`,
      );
      if (!fichero) {
        notificar.error('NO SE PUDO GENERAR EL FICHERO');
        return;
      }

      const url = URL.createObjectURL(fichero);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `cartera-${resumen.data?.corte ?? 'hoy'}.${formato}`;
      enlace.click();
      URL.revokeObjectURL(url);

      notificar.exito(`Cartera descargada en ${formato.toUpperCase()}`);
    } catch (causa) {
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO DESCARGAR LA CARTERA',
      );
    } finally {
      setBajando(null);
    }
  }

  const r = resumen.data;
  const porVencer = r?.tramos.find((t) => t.codigo === 'por_vencer');

  return (
    <>
      <EncabezadoDePagina
        icono={Wallet}
        titulo="Cartera"
        subtitulo="Lo que se debe, del sistema anterior y del nuevo"
        acciones={
          <span className="flex items-center gap-2">
            <Descarga
              formato="pdf"
              icono={FileText}
              titulo="Descargar en PDF: el resumen y los cincuenta que más deben"
              bajando={bajando}
              alPulsar={() => void descargar('pdf')}
              clase="hover:border-red-300 hover:text-red-600"
            />
            <Descarga
              formato="xlsx"
              icono={FileSpreadsheet}
              titulo="Descargar en Excel: el listado completo de deudores"
              bajando={bajando}
              alPulsar={() => void descargar('xlsx')}
              clase="hover:border-emerald-300 hover:text-emerald-600"
            />
          </span>
        }
      />

      {/*
        Los filtros bajan al detalle de la factura: el servicio vive en la linea, no en la
        cabecera, porque una factura del contribuyente puede llevar la tasa mensual de tres
        predios y un servicio especial. Sin filtros se cuenta por factura, como se cobra.
      */}
      <div className="mb-5 flex flex-wrap items-end gap-3 rounded-xl border border-armazon-200 bg-white p-4">
        <label className="grid gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-armazon-600">
            Servicio
          </span>
          <select
            value={servicio}
            onChange={(e) => filtrar(() => setServicio(e.target.value))}
            className="h-10 min-w-56 rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none focus:border-marca-400"
          >
            <option value="">Todos los servicios</option>
            {(servicios.data ?? []).map((s) => (
              <option key={s.codigo} value={s.codigo}>
                {s.nombre}
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-armazon-600">
            Desde
          </span>
          <input
            type="month"
            value={desde}
            onChange={(e) => filtrar(() => setDesde(e.target.value))}
            className="h-10 rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none focus:border-marca-400"
          />
        </label>

        <label className="grid gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-armazon-600">
            Hasta
          </span>
          <input
            type="month"
            value={hasta}
            onChange={(e) => filtrar(() => setHasta(e.target.value))}
            className="h-10 rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none focus:border-marca-400"
          />
        </label>

        {hayFiltros && (
          <button
            type="button"
            onClick={() =>
              filtrar(() => {
                setServicio('');
                setDesde('');
                setHasta('');
              })
            }
            className="h-10 rounded-lg border border-armazon-300 px-4 text-xs font-semibold uppercase tracking-wide text-armazon-700 transition-colors hover:bg-armazon-100"
          >
            Quitar filtros
          </button>
        )}
      </div>

      {r?.sinIntereses && (
        <div className="mb-5 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <Info className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
          <p className="text-justify text-xs uppercase leading-relaxed text-blue-800">
            <span className="font-semibold">Estas cifras son el capital, sin intereses.</span> El
            interés por mora todavía no se calcula: falta publicar las tasas y falta saber qué
            clientes son empresas públicas, a las que la ordenanza no se lo cobra. Lo que se debe de
            capital sí es exacto.
          </p>
        </div>
      )}

      {resumen.isPending ? (
        <div className="mb-5">
          <Cargando
            titulo="Consultando la cartera"
            detalle="Sumando la deuda de las dos tablas"
            alto="h-28"
          />
        </div>
      ) : (
        r && (
          <>
            <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Indicador
                valor={r.total}
                rotulo="Deuda total"
                nota={`${r.facturas.toLocaleString('es-EC')} facturas`}
              />
              <Indicador
                valor={r.enMora}
                rotulo="Fuera de plazo"
                nota="Sobre esto correría el interés"
                alerta
              />
              <Indicador
                valor={porVencer?.monto ?? 0}
                rotulo="Todavía en plazo"
                nota={`${(porVencer?.facturas ?? 0).toLocaleString('es-EC')} facturas`}
              />
              <Indicador
                valor={deudores.data?.totalElementos ?? 0}
                rotulo="Personas que deben"
                nota={`Al corte del ${formatearCorto(r.corte)}`}
                cuenta
              />
            </div>

            <div className="mb-5 rounded-2xl border border-armazon-200 bg-white p-5">
              <p className="mb-4 text-[11px] font-semibold uppercase tracking-wide text-armazon-600">
                Por antigüedad
              </p>

              <div className="space-y-3">
                {r.tramos.map((t) => (
                  <div key={t.codigo} className="flex flex-wrap items-center gap-3">
                    <span className="w-28 shrink-0 text-[11px] font-semibold uppercase text-armazon-700">
                      {t.nombre}
                    </span>

                    <span className="relative h-6 min-w-0 flex-1 overflow-hidden rounded-md bg-armazon-100">
                      <span
                        className={`absolute inset-y-0 left-0 ${
                          t.enMora ? 'bg-amber-500' : 'bg-armazon-500'
                        }`}
                        style={{
                          width: r.total > 0 ? `${(t.monto / r.total) * 100}%` : '0%',
                        }}
                      />
                    </span>

                    <span className="importe w-28 shrink-0 text-right text-sm font-semibold text-armazon-800">
                      ${t.monto.toFixed(2)}
                    </span>
                    <span className="w-24 shrink-0 text-right text-[10px] uppercase text-armazon-400">
                      {t.facturas.toLocaleString('es-EC')} fact.
                    </span>
                  </div>
                ))}
              </div>

              <p className="mt-4 border-t border-armazon-200 pt-3 text-justify text-[10px] uppercase leading-relaxed text-armazon-500">
                La antigüedad se mide contra la fecha de vencimiento de cada factura al corte, no
                contra un estado guardado. El sistema anterior lo guardaba y su marca de vencidas
                erraba en 921 facturas. El número de personas de cada tramo no se puede sumar entre
                tramos: una misma persona puede deber en varios.
              </p>
            </div>
          </>
        )
      )}

      <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-armazon-100 px-6 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-armazon-600">
            Quién debe
          </p>
          <p className="text-[10px] uppercase text-armazon-400">Del que más debe al que menos</p>
        </div>

        {deudores.isPending ? (
          <CargandoEnLinea detalle="Ordenando a los deudores" />
        ) : (
          <>
            <div className="divide-y divide-armazon-100">
              {(deudores.data?.contenido ?? []).map((d) => (
                <Deudor key={d.clienteId} deudor={d} />
              ))}
            </div>

            <Paginador
              pagina={pagina}
              porPagina={POR_PAGINA}
              total={deudores.data?.totalElementos ?? 0}
              quePagina="deudores"
              alCambiar={setPagina}
            />
          </>
        )}
      </div>
    </>
  );
}

function Descarga({
  formato,
  icono: Icono,
  titulo,
  bajando,
  alPulsar,
  clase,
}: {
  formato: 'pdf' | 'xlsx';
  icono: typeof FileText;
  titulo: string;
  bajando: 'pdf' | 'xlsx' | null;
  alPulsar: () => void;
  clase: string;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      disabled={bajando !== null}
      title={titulo}
      aria-label={titulo}
      className={`grid size-10 place-items-center rounded-lg border border-armazon-200 bg-white text-armazon-600 transition-colors disabled:opacity-40 ${clase}`}
    >
      {bajando === formato ? (
        <Loader2 className="size-[18px] animate-spin" aria-hidden />
      ) : (
        <Icono className="size-[18px]" aria-hidden />
      )}
    </button>
  );
}

function Deudor({ deudor: d }: { deudor: DeudorEnCartera }) {
  const esInstitucion = (d.identificacion ?? '').length === 13;

  return (
    <div className="flex flex-wrap items-center gap-4 px-6 py-3 transition-colors hover:bg-armazon-50/60">
      <span
        className={`grid size-8 shrink-0 place-items-center rounded-lg ${
          esInstitucion ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-500'
        }`}
      >
        {esInstitucion ? (
          <Building2 className="size-4" aria-hidden />
        ) : (
          <UsuarioIcono className="size-4" aria-hidden />
        )}
      </span>

      <span className="min-w-0 flex-[2] basis-64">
        <span className="block truncate text-sm font-medium uppercase text-armazon-800">
          {d.nombre ?? `Cliente ${d.clienteId} (ya no está en el catastro)`}
        </span>
        <span className="importe block text-[11px] text-armazon-400">{d.identificacion}</span>
      </span>

      <span className="w-28 shrink-0 text-[11px] uppercase text-armazon-500">
        {d.facturas.toLocaleString('es-EC')} facturas
      </span>

      <span className="w-32 shrink-0 text-[11px] uppercase text-armazon-500">
        Desde {formatearCorto(d.masVieja)}
      </span>

      <span className="importe w-28 shrink-0 text-right text-sm font-bold text-armazon-800">
        ${d.total.toFixed(2)}
      </span>
    </div>
  );
}

function Indicador({
  valor,
  rotulo,
  nota,
  alerta = false,
  cuenta = false,
}: {
  valor: number;
  rotulo: string;
  nota: string;
  alerta?: boolean;
  cuenta?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        alerta ? 'border-amber-200 bg-amber-50' : 'border-armazon-200 bg-white'
      }`}
    >
      <span
        className={`importe block text-xl font-bold ${
          alerta ? 'text-amber-700' : 'text-armazon-800'
        }`}
      >
        {cuenta ? valor.toLocaleString('es-EC') : `$${valor.toFixed(2)}`}
      </span>
      <span className="block text-[10px] font-semibold uppercase text-armazon-600">{rotulo}</span>
      <span className="block text-[9px] uppercase text-armazon-400">{nota}</span>
      {alerta && <AlertTriangle className="mt-1 size-3 text-amber-600" aria-hidden />}
    </div>
  );
}

function formatearCorto(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
